import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogIn, UserPlus, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LOGO } from "../lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const [tab, setTab] = useState("signin");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = new URLSearchParams(location.search).get("next") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user =
        tab === "signin"
          ? await login(form.email, form.password)
          : await register(form.name, form.email, form.password);
      toast.success(`Welcome, ${user.name.split(" ")[0]}`);
      navigate(user.is_admin ? "/admin" : nextPath);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#0B1526]">
        <img src="https://images.unsplash.com/photo-1756136836696-0e3374ebfbac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1F3864]/80 to-[#0B1526]/90" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="" className="h-12 w-12 bg-white/5 p-1 rounded-sm" />
            <div>
              <div className="font-display text-xl">Next Gen Praise</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#C00000]">Praise Beyond Limits</div>
            </div>
          </Link>
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Welcome home</div>
            <h1 className="font-display text-5xl lg:text-6xl leading-[1.02] mb-5">
              Praise <span className="italic text-[#C00000]">Beyond Limits.</span>
            </h1>
            <p className="text-white/70 max-w-md leading-relaxed">
              Sign in to unlock exclusive videos, manage your purchases, and go deeper into the
              movement.
            </p>
          </div>
          <div className="text-xs text-white/40">© 2026 Next Gen Praise NPC</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F5F5F5]">
        <div className="w-full max-w-md" data-testid="auth-panel">
          <div className="flex border border-gray-300 mb-8">
            <button
              onClick={() => setTab("signin")}
              className={`flex-1 py-3 text-xs tracking-[0.2em] uppercase ${tab === "signin" ? "bg-[#1F3864] text-white" : "text-gray-700"}`}
              data-testid="auth-tab-signin"
            >Sign In</button>
            <button
              onClick={() => setTab("signup")}
              className={`flex-1 py-3 text-xs tracking-[0.2em] uppercase ${tab === "signup" ? "bg-[#1F3864] text-white" : "text-gray-700"}`}
              data-testid="auth-tab-signup"
            >Create Account</button>
          </div>

          <h2 className="font-display text-3xl text-[#1F3864] mb-6">
            {tab === "signin" ? "Welcome back." : "Join the movement."}
          </h2>

          <button
            onClick={googleSignIn}
            className="w-full py-3 border-2 border-gray-300 hover:border-[#1F3864] bg-white transition-colors flex items-center justify-center gap-3 text-sm font-medium mb-6"
            data-testid="action-google-signin-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.5-.2-2.2H12v4.2h5.9c-.3 1.4-1 2.5-2.2 3.3v2.7h3.5c2.1-1.9 3.3-4.7 3.3-8z"/><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.3 1.1-3.8 1.1-2.9 0-5.3-1.9-6.2-4.6H2.2v2.9C4 20.2 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.8 14.1c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1V7H2.2C1.4 8.5 1 10.2 1 12s.4 3.5 1.2 5l3.6-2.9z"/><path fill="#EA4335" d="M12 5.3c1.6 0 3.1.6 4.2 1.6l3.1-3.1C17.5 2 15 1 12 1 7.7 1 4 3.8 2.2 7l3.6 2.9C6.7 7.2 9.1 5.3 12 5.3z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-6 text-xs text-gray-500">
            <div className="flex-1 h-px bg-gray-300" />
            OR
            <div className="flex-1 h-px bg-gray-300" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid={tab === "signin" ? "form-login" : "form-register"}>
            {tab === "signup" && (
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] bg-white outline-none text-sm" data-testid="input-name" />
            )}
            <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] bg-white outline-none text-sm" data-testid="input-email" />
            <input required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] bg-white outline-none text-sm" data-testid="input-password" />
            <button type="submit" disabled={busy} className="btn-primary-red w-full justify-center !py-3.5" data-testid={tab === "signin" ? "action-signin-submit" : "action-signup-submit"}>
              {tab === "signin" ? <LogIn className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
              {busy ? "..." : tab === "signin" ? "Sign In" : "Create Account"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-8 text-xs text-gray-500 text-center">
            By continuing you agree to our Terms of Service and Privacy Policy.
          </div>
        </div>
      </div>
    </div>
  );
}
